import { uniq } from 'es-toolkit';
import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { Autocomplete, Box, Checkbox, Divider, FormControlLabel, Grid, IconButton, TextField } from '@mui/material';

import { PropertyTextInput } from '@paragon/client-tools';
import {
  ProductionArtifactsContext,
  ProductionOutputParameters,
  ProductionOutputType,
} from '@paragon/contract-library';
import { NumericField } from '@paragon/material';
import { InlineNotification } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { humanCase, naturalSortByProperty } from '@paragon/utilities';

import { downloadProductionArtifacts } from '../../actions/downloadActions';
import { createProductionOutputPreset, deleteProductionOutputPreset } from '../../actions/productionOutputPresets';
import { useAppDispatch, useAppSelector } from '../hooks';

const allProductionOutputTypes: readonly ProductionOutputType[] = [
  'BundleLabels',
  'ComponentWrapLabels',
  'FloorChordPullList',
  'WebAndBlockCutSheets',
  'WipLabels',
  'ProductionAndShippingList',
  'CartLabels',
  'ComponentReport',
  'LumberPickList',
  'PlatePickList',
  'TpsFiles',
  'TreFiles',
  'TrsMachineryFiles',
];

function getDisplayName(productionOutputType: ProductionOutputType): string {
  switch (productionOutputType) {
    case 'TpsFiles':
      return 'TPS Files';

    case 'TreFiles':
      return 'TRE Files';

    case 'TrsMachineryFiles':
      return 'TRS Machinery Files';

    default:
      return humanCase(productionOutputType);
  }
}

export interface DownloadProductionPackageDialogProps {
  readonly bundleGuids: readonly string[];

  /** If this property is specified, presets can not be used. If this property is not specified, all output types are
   * available and presets can be used.
   */
  readonly availableOutputTypes?: readonly ProductionOutputType[];
  readonly productionArtifactsContext: ProductionArtifactsContext;
}

export function DownloadProductionPackageDialog(props: DownloadProductionPackageDialogProps) {
  const { availableOutputTypes, bundleGuids, productionArtifactsContext } = props;
  const dispatch = useAppDispatch();
  const productionOutputPresets = useAppSelector((state) => state.productionOutputPresets);

  const productionOutputTypesList = availableOutputTypes ?? allProductionOutputTypes;
  const [selectedProductionOutputPresetGuid, setSelectedProductionOutputPresetGuid] = useState<string | null>(null);
  const [newPresetName, setNewPresetName] = useState<string | null>(null);
  const [productionOutputTypes, setProductionOutputTypes] = useState<readonly ProductionOutputType[]>([]);
  const [productionOutputParameters, setProductionOutputParameters] = useState<ProductionOutputParameters>({
    componentLabelCountPerComponent: 1,
  });

  const newPresetNameMatchesExisting = Object.values(productionOutputPresets).some(
    (productionOutputPreset) => productionOutputPreset.name === newPresetName,
  );
  const allSelected = productionOutputTypesList.every((outputType) => productionOutputTypes.includes(outputType));
  const someSelected = productionOutputTypesList.some((outputType) => productionOutputTypes.includes(outputType));

  function checkBoxForProductionOutputType(productionOutputType: ProductionOutputType) {
    return (
      <FormControlLabel
        key={productionOutputType}
        control={
          <Checkbox
            checked={productionOutputTypes.includes(productionOutputType)}
            onChange={(event) =>
              void setProductionOutputTypes((oldState) =>
                event.target.checked
                  ? uniq([...oldState, productionOutputType])
                  : oldState.filter(
                      (checkedProductionOutputType) => checkedProductionOutputType !== productionOutputType,
                    ),
              )
            }
          />
        }
        label={getDisplayName(productionOutputType)}
      />
    );
  }

  return (
    <Dialog
      title="Download Production Package"
      open
      submitText="Download"
      onSubmit={() => {
        dispatch(
          downloadProductionArtifacts(
            bundleGuids,
            productionOutputTypes,
            productionArtifactsContext,
            productionOutputParameters,
          ),
        );
        dispatch(closeCurrentPopup());
      }}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '500px', height: '700px' }}
      submitDisabled={productionOutputTypes.length === 0}
      enterKeySubmits
    >
      {availableOutputTypes === undefined && (
        <>
          <Grid container sx={{ mt: 1, mb: 1 }} spacing={2}>
            <Grid size={11}>
              <Autocomplete
                size="small"
                options={naturalSortByProperty(
                  Object.values(productionOutputPresets),
                  (productionOutputPreset) => productionOutputPreset.name,
                )}
                getOptionLabel={(productionOutputPreset) => productionOutputPreset.name}
                renderOption={(props, option) => (
                  <li {...props} key={option.guid}>
                    {option.name}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Select Preset to Apply" />}
                value={
                  selectedProductionOutputPresetGuid
                    ? productionOutputPresets[selectedProductionOutputPresetGuid]
                    : null
                }
                onChange={(event, productionOutputPreset) => {
                  setSelectedProductionOutputPresetGuid(productionOutputPreset?.guid ?? null);
                  if (productionOutputPreset) {
                    setProductionOutputTypes(productionOutputPreset.outputTypes);
                    setProductionOutputParameters(productionOutputPreset.parameters);
                  }
                }}
              />
            </Grid>
            <Grid size={1} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
              <IconButton
                onClick={() => {
                  if (selectedProductionOutputPresetGuid === null) return;
                  dispatch(deleteProductionOutputPreset(selectedProductionOutputPresetGuid));
                  setSelectedProductionOutputPresetGuid(null);
                }}
                disabled={selectedProductionOutputPresetGuid === null}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid size={11}>
                <PropertyTextInput
                  property="New Preset Name"
                  value={newPresetName ?? ''}
                  submit={setNewPresetName}
                  fullWidth
                />
              </Grid>
              <Grid size={1} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
                <IconButton
                  onClick={async () => {
                    if (newPresetName === null) return;

                    // Clear the preset name first to prevent flashing an error message briefly
                    const presetNameToSave = newPresetName;
                    setNewPresetName(null);

                    const newProductionOutputPreset = await dispatch(
                      createProductionOutputPreset(presetNameToSave, productionOutputTypes, productionOutputParameters),
                    );
                    if (newProductionOutputPreset) {
                      setSelectedProductionOutputPresetGuid(newProductionOutputPreset.guid);
                    }
                  }}
                  disabled={
                    newPresetName === null || newPresetNameMatchesExisting || productionOutputTypes.length === 0
                  }
                >
                  <SaveIcon />
                </IconButton>
              </Grid>
            </Grid>
            {newPresetNameMatchesExisting && (
              <InlineNotification type="error">
                Name matches existing preset. Choose a different name.
              </InlineNotification>
            )}
          </Box>
        </>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={allSelected}
            indeterminate={allSelected ? false : someSelected}
            onChange={() => void setProductionOutputTypes(allSelected ? [] : productionOutputTypesList)}
          />
        }
        label="Select All"
      />
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={1} sx={{ alignItems: 'center' }}>
          {productionOutputTypesList.map((productionOutputType) =>
            productionOutputType === 'ComponentWrapLabels' ? (
              <>
                <Grid size={{ xs: 6 }}>{checkBoxForProductionOutputType(productionOutputType)}</Grid>
                <Grid size={{ xs: 6 }}>
                  <NumericField
                    label="Count per component"
                    value={productionOutputParameters.componentLabelCountPerComponent}
                    integers
                    min={1}
                    onSubmit={(componentLabelCountPerComponent) =>
                      setProductionOutputParameters((oldState) => ({ ...oldState, componentLabelCountPerComponent }))
                    }
                    disabled={!productionOutputTypes.includes(productionOutputType)}
                  />
                </Grid>
              </>
            ) : (
              <Grid size={{ xs: 12 }}>{checkBoxForProductionOutputType(productionOutputType)}</Grid>
            ),
          )}
        </Grid>
      </Box>
    </Dialog>
  );
}
